// The human check (Turnstile) runs in a frame on its own origin
// (apps/challenge, docs/adr/0007), so Cloudflare's script never shares a
// page with a secret or a link. These helpers build the frame URL and read
// its messages; hooks/useHumanCheck.ts wires them up.

import type { ChallengeAction } from '@sss/types';

export type ChallengeTheme = 'light' | 'dark' | 'auto';

export type ChallengeMessage = { type: 'token'; token: string } | { type: 'expired' } | { type: 'interactive' } | { type: 'error' };

const MAX_TOKEN_LENGTH = 2048;

/** URL of the challenge page for this parent origin, action and theme. */
export const challengeFrameUrl = (challengeUrl: string, parentOrigin: string, action: ChallengeAction, theme: ChallengeTheme): string => {
  const url = new URL(challengeUrl);
  url.pathname = '/';
  url.search = new URLSearchParams({ origin: parentOrigin, action, theme }).toString();
  return url.toString();
};

/** A message from the challenge frame, or null for anything else (other frames, extensions, junk). */
export const parseChallengeMessage = (data: unknown): ChallengeMessage | null => {
  if (typeof data !== 'object' || data === null) return null;
  const { source, type, token } = data as Record<string, unknown>;
  if (source !== 'sss-challenge') return null;
  if (type === 'token') {
    return typeof token === 'string' && token.length > 0 && token.length <= MAX_TOKEN_LENGTH ? { type, token } : null;
  }
  if (type === 'expired' || type === 'interactive' || type === 'error') return { type };
  return null;
};

/** Turnstile's theme for the page as shown: a forced light/dark choice, else follow the OS. */
export const challengeTheme = (root: Pick<HTMLElement, 'getAttribute'>): ChallengeTheme => {
  const forced = root.getAttribute('data-theme');
  return forced === 'light' || forced === 'dark' ? forced : 'auto';
};
